/**
 * Identity guard which always returns true
 * @returns true
 */
export const always = () => true;

/**
 * Negates the supplied guards.
 * @example ```typescript
 *  const yeah = () => true;
 *  const yeahnah = not(yeah);
 *
 *  yeahnah(); // → false (or does it?)
 *```
 */
export const not =
  (guard: (...args: any[]) => boolean) =>
  (...args: any[]) =>
    !guard(...args);

/**
 * Log the guard name and its result for debugging purposes.
 * @example ```typescript
 *  const yeah = () => true;
 *  const yeahnah = not(yeah);
 *
 *  debug(yeahnah)(); // { guard: 'yeannah', passed: true }
 *```
 */
export const debug =
  (guard: (...args: any[]) => boolean) =>
  (...args: any[]) => {
    const result = guard(...args);
    console.log({ guard: guard.name, passed: result, args });
    return result;
  };

/**
 * ORs the supplied guards..
 * @example ```typescript
 *  const yeah = () => true;
 *  const nah = () => false;
 *
 *  const yeahnah = or(yeah, nah);
 *
 *  yeahnah(); // → true (or... does it?)
 *```
 */
export const or = (...guards: ((...args: any[]) => boolean)[]) => {
  return (...args: any[]) => guards.some((guard) => guard(...args));
};

/**
 * ANDs the supplied guards..
 * @example ```typescript
 *  const yeah = () => true;
 *  const nah = () => false;
 *
 *  const yeahnah = and(yeah, nah);
 *
 *  yeahnah(); // → false
 *```
 */
export const and = (...guards: ((...args: any[]) => boolean)[]) => {
  return (...args: any[]) => guards.every((guard) => guard(...args));
};
